import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Banknote, RefreshCw, Download, Upload, CheckCircle2, XCircle,
  FlaskConical, AlertCircle, Clock, ListChecks, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

export default function Ebics() {
  const { company } = useCompany();
  const [banks, setBanks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [bank, setBank] = useState("");
  const [hostUrl, setHostUrl] = useState("");
  const [hostId, setHostId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [userId, setUserId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [testMode, setTestMode] = useState(true);
  const [autoDownload, setAutoDownload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerJob, setDrawerJob] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const openJobDrawer = useCallback(async (job) => {
    if (!company?._id) return;
    setDrawerOpen(true);
    setDrawerJob(null);
    setDrawerLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/ebics/jobs/${job.id}`);
      setDrawerJob(res.data);
    } catch (e) {
      toast.error("Konnte Job-Details nicht laden.");
      setDrawerOpen(false);
    } finally {
      setDrawerLoading(false);
    }
  }, [company?._id]);

  const loadBanks = useCallback(async () => {
    try {
      const res = await apiClient.get(`/ebics/banks`);
      setBanks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // silent
    }
  }, []);

  const load = useCallback(async () => {
    if (!company?._id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/ebics-settings`);
      setSettings(res.data);
      setBank(res.data.bank || "");
      setHostUrl(res.data.host_url || "");
      setHostId(res.data.host_id || "");
      setPartnerId(res.data.partner_id || "");
      setUserId(res.data.user_id || "");
      setEnabled(!!res.data.enabled);
      setTestMode(res.data.test_mode !== false);
      setAutoDownload(!!res.data.auto_download_enabled);
    } catch (e) {
      toast.error("Konnte EBICS-Einstellungen nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [company?._id]);

  const loadJobs = useCallback(async () => {
    if (!company?._id) return;
    setJobsLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/ebics/jobs`, {
        params: { limit: 30 },
      });
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // silent
    } finally {
      setJobsLoading(false);
    }
  }, [company?._id]);

  useEffect(() => { loadBanks(); }, [loadBanks]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleBankChange = (id) => {
    setBank(id);
    const preset = banks.find((b) => b.id === id);
    if (preset && !hostUrl) setHostUrl(preset.host_url || "");
    if (preset && !hostId) setHostId(preset.host_id || "");
  };

  const handleSave = async () => {
    if (!company?._id) return;
    setSaving(true);
    try {
      const body = {
        enabled,
        bank: bank || null,
        host_url: hostUrl.trim() || null,
        host_id: hostId.trim() || null,
        partner_id: partnerId.trim() || null,
        user_id: userId.trim() || null,
        test_mode: testMode,
        auto_download_enabled: autoDownload,
      };
      const res = await apiClient.put(`/companies/${company._id}/ebics-settings`, body);
      setSettings(res.data);
      toast.success("EBICS-Einstellungen gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const handleDownloadNow = async () => {
    if (!company?._id) return;
    setDownloading(true);
    try {
      const res = await apiClient.post(`/companies/${company._id}/ebics/download-camt053`);
      const d = res.data;
      if (d.success) {
        const mode = d.mocked ? "Mock" : "Live";
        toast.success(
          `EBICS Download (${mode}): ${d.imported_count} importiert, ${d.skipped_duplicates} Doubletten.`,
        );
        loadJobs();
      } else {
        toast.error(`Download fehlgeschlagen: ${d.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen.");
    } finally { setDownloading(false); }
  };

  const [testing, setTesting] = useState(false);
  const handleTestConnection = async () => {
    if (!company?._id) return;
    setTesting(true);
    try {
      const res = await apiClient.post(`/companies/${company._id}/ebics/test-connection`);
      const d = res.data;
      if (d.success) {
        toast.success(`✅ ${d.mode === "mock" ? "Mock" : "Live"}-Verbindung OK: ${d.message}`);
      } else {
        toast.error(`Verbindungstest fehlgeschlagen: ${d.message}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verbindungstest fehlgeschlagen.");
    } finally { setTesting(false); }
  };

  if (loading) {
    return <div className="p-6 text-slate-500 text-sm" data-testid="ebics-loading">Lade…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="ebics-page">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="text-sm text-slate-500 hover:text-slate-900" data-testid="ebics-back-link">
          ← Einstellungen
        </Link>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-500 flex items-center justify-center text-white shadow-sm">
          <Banknote className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">EBICS Bank-Connector</h1>
          <p className="text-sm text-slate-500 mt-1">
            Direkte Bank-Anbindung via EBICS-Protokoll. camt.053 automatisch täglich abholen,
            pain.001 (LSV/Überweisungen) direkt einreichen. Unterstützt: UBS, Raiffeisen,
            PostFinance, ZKB.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={settings?.enabled
            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : "bg-slate-100 text-slate-600 border-slate-200"
          } data-testid="ebics-status-badge">
            {settings?.enabled ? "AKTIV" : "INAKTIV"}
          </Badge>
          {settings?.test_mode !== false && (
            <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[10px]">
              <FlaskConical className="h-2.5 w-2.5 mr-0.5" /> TEST-MODUS
            </Badge>
          )}
        </div>
      </div>

      <Card className="p-6 space-y-5 border border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Connector aktivieren</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Wenn aktiv erscheinen "EBICS"-Buttons auf Rechnungen + Bank-Reconciliation.
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ebics-enabled-switch" />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              Test-Modus
              {testMode && <Badge className="bg-amber-100 text-amber-900 text-[10px]">MOCK</Badge>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Im Test-Modus werden synthetische camt.053-Buchungen geliefert und Uploads geloggt
              statt zur Bank gesendet.
            </p>
          </div>
          <Switch checked={testMode} onCheckedChange={setTestMode} data-testid="ebics-test-mode-switch" />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              Auto-Download (alle 6h)
              <Clock className="h-3.5 w-3.5 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Holt camt.053 automatisch alle 6 Stunden und importiert neue Buchungen.
              {settings?.last_auto_download_at && (
                <span className="ml-1 text-emerald-700">
                  · Zuletzt: {new Date(settings.last_auto_download_at).toLocaleString("de-CH")}
                </span>
              )}
            </p>
          </div>
          <Switch
            checked={autoDownload}
            onCheckedChange={setAutoDownload}
            data-testid="ebics-auto-download-switch"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Bank</label>
            <Select value={bank} onValueChange={handleBankChange}>
              <SelectTrigger data-testid="ebics-bank-select">
                <SelectValue placeholder="Bank wählen" />
              </SelectTrigger>
              <SelectContent>
                {banks.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Host-URL und Host-ID werden bei Auswahl vorbefüllt.</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Host-ID</label>
            <Input
              type="text"
              placeholder="z.B. UBSWCHZH"
              value={hostId}
              onChange={(e) => setHostId(e.target.value)}
              data-testid="ebics-host-id-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Host-URL (EBICS Endpoint)</label>
          <Input
            type="text"
            placeholder="https://ebics.bank.ch/ebicsweb/ebicsweb"
            value={hostUrl}
            onChange={(e) => setHostUrl(e.target.value)}
            data-testid="ebics-host-url-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Partner-ID</label>
            <Input
              type="text"
              placeholder="z.B. CUST12345"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              data-testid="ebics-partner-id-input"
            />
            <p className="text-xs text-slate-500">Wird Dir von der Bank vergeben.</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">User-ID</label>
            <Input
              type="text"
              placeholder="z.B. USR001"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="ebics-user-id-input"
            />
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button onClick={load} variant="outline" disabled={saving} data-testid="ebics-reset-btn">
            Verwerfen
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="ebics-save-btn">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Card>

      {/* Manual Actions */}
      <Card className="p-6 border border-slate-200 bg-white" data-testid="ebics-actions-card">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Manuelle Aktionen</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleDownloadNow}
            disabled={downloading || !enabled}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ebics-download-now-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Lade…" : "camt.053 jetzt abholen"}
          </Button>
          <Button
            onClick={handleTestConnection}
            disabled={testing || !enabled}
            variant="outline"
            data-testid="ebics-test-connection-btn"
          >
            <FlaskConical className="w-4 h-4 mr-2" />
            {testing ? "Teste…" : "Verbindung testen"}
          </Button>
          <span className="text-xs text-slate-500">
            <Upload className="w-3 h-3 inline mr-1" />
            pain.001-Upload erfolgt aus der Rechnungs-Detailansicht ("EBICS senden")
          </span>
        </div>
        {!enabled && (
          <div className="mt-3 text-xs text-amber-700 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Aktiviere den Connector oben, um Downloads auszuführen.
          </div>
        )}
      </Card>

      {/* Job-Log */}
      <Card className="p-6 border border-slate-200 bg-white" data-testid="ebics-job-log-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Job-Log (letzte 30)</h2>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadJobs}
            disabled={jobsLoading}
            data-testid="ebics-job-log-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${jobsLoading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
        {jobs.length === 0 ? (
          <div
            className="text-sm text-slate-500 italic py-6 text-center"
            data-testid="ebics-job-log-empty"
          >
            Noch keine Jobs — starte einen Download oder warte auf den 6h-Cron.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="ebics-job-log-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Typ</th>
                  <th className="py-2 px-2">Zeit</th>
                  <th className="py-2 px-2">Order</th>
                  <th className="py-2 px-2">Modus</th>
                  <th className="py-2 px-2">Quelle</th>
                  <th className="py-2 px-2">Importiert</th>
                  <th className="py-2 px-2">Fehler</th>
                  <th className="py-2 px-2 text-right">Details</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr
                    key={j.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`ebics-job-row-${j.id}`}
                  >
                    <td className="py-2 px-2">
                      {j.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-700">
                          <XCircle className="w-3.5 h-3.5" /> Fehler
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">
                      {j.job_type === "download_camt053" ? (
                        <span className="inline-flex items-center gap-1">
                          <Download className="w-3 h-3" /> Download
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-slate-600 font-mono text-xs">
                      {j.run_at ? new Date(j.run_at).toLocaleString("de-CH") : "—"}
                    </td>
                    <td className="py-2 px-2 text-slate-600 text-xs">{j.order_type || "—"}</td>
                    <td className="py-2 px-2">
                      {j.mocked ? (
                        <Badge className="bg-amber-100 text-amber-900 text-[10px]">MOCK</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">LIVE</Badge>
                      )}
                    </td>
                    <td className="py-2 px-2 text-slate-600 text-xs">{j.triggered_by || "—"}</td>
                    <td className="py-2 px-2 text-slate-700 font-mono text-xs">
                      {j.imported_count != null ? j.imported_count : "—"}
                    </td>
                    <td className="py-2 px-2 text-rose-700 text-xs max-w-[200px] truncate" title={j.error || ""}>
                      {j.error || ""}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openJobDrawer(j)}
                        data-testid={`ebics-job-detail-btn-${j.id}`}
                        title="Job-Details + XML-Preview"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Iter62: Job-Detail-Drawer mit XML-Preview */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="ebics-job-drawer"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {drawerJob?.job_type === "upload_pain001" ? (
                <Upload className="w-5 h-5 text-emerald-600" />
              ) : (
                <Download className="w-5 h-5 text-emerald-600" />
              )}
              EBICS Job-Details
            </SheetTitle>
            <SheetDescription>
              {drawerJob?.run_at
                ? new Date(drawerJob.run_at).toLocaleString("de-CH")
                : "—"}
            </SheetDescription>
          </SheetHeader>
          {drawerLoading ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="ebics-job-drawer-loading">
              Lade Details…
            </div>
          ) : drawerJob ? (
            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Status</div>
                  <div className="font-medium">
                    {drawerJob.success ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" /> Erfolgreich
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-700">
                        <XCircle className="w-4 h-4" /> Fehler
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Modus</div>
                  <div>
                    {drawerJob.mocked ? (
                      <Badge className="bg-amber-100 text-amber-900 text-[10px]">MOCK</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">LIVE</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Job-Typ</div>
                  <div className="font-medium">{drawerJob.job_type}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Order-Type</div>
                  <div className="font-mono text-xs">{drawerJob.order_type || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Quelle</div>
                  <div className="font-mono text-xs">{drawerJob.triggered_by || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">XML-Größe</div>
                  <div className="font-mono text-xs">{drawerJob.size ?? 0} Bytes</div>
                </div>
                {drawerJob.imported_count != null && (
                  <div>
                    <div className="text-xs text-slate-500">Importiert</div>
                    <div className="font-mono text-xs">{drawerJob.imported_count}</div>
                  </div>
                )}
                {drawerJob.transaction_id && (
                  <div>
                    <div className="text-xs text-slate-500">Transaction-ID</div>
                    <div className="font-mono text-xs break-all">{drawerJob.transaction_id}</div>
                  </div>
                )}
                {drawerJob.invoice_id && (
                  <div className="col-span-2">
                    <div className="text-xs text-slate-500">Rechnung</div>
                    <div className="font-mono text-xs">{drawerJob.invoice_id}</div>
                  </div>
                )}
              </div>

              {drawerJob.error && (
                <div className="p-3 rounded bg-rose-50 border border-rose-200 text-xs text-rose-800" data-testid="ebics-job-drawer-error">
                  <div className="font-semibold mb-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Fehler-Meldung
                  </div>
                  {drawerJob.error}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-slate-700">XML-Preview (erste 8 KB)</div>
                  {drawerJob.xml_preview && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(drawerJob.xml_preview);
                        toast.success("XML kopiert.");
                      }}
                      data-testid="ebics-job-drawer-copy-xml"
                    >
                      Kopieren
                    </Button>
                  )}
                </div>
                {drawerJob.xml_preview ? (
                  <pre
                    className="bg-slate-900 text-emerald-200 text-[10px] font-mono p-3 rounded overflow-auto max-h-[400px] whitespace-pre-wrap break-all"
                    data-testid="ebics-job-drawer-xml"
                  >
                    {drawerJob.xml_preview}
                  </pre>
                ) : (
                  <div className="text-xs text-slate-400 italic" data-testid="ebics-job-drawer-no-xml">
                    Keine XML-Daten verfügbar.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
